<div class="page-container">
  <!-- Main Content -->
  <main class="page-main">
    <div class="page-content">
      <div class="page-title-container">
        <app-breadcrumb></app-breadcrumb>
        <h1 class="page-title">User Profile</h1>
      </div>

      <!-- User Information Cards -->
      <div class="stats-grid">
        <!-- User Profile Card -->
        <mat-card class="profile-card">
          <div class="card-header">
            <div class="card-info">
              <div class="card-label">Account Information</div>
              <div class="card-value">{{ userName }}</div>
            </div>
            <div class="card-icon blue">
              <mat-icon>account_circle</mat-icon>
            </div>
          </div>
          <div class="card-footer">
            <span class="card-meta">{{ userEmail }}</span>
          </div>
        </mat-card>

        <!-- Skills & Attributes Card (self-service; requires an employee record) -->
        @if (employeeId) {
          <mat-card class="profile-card attributes-card">
            <app-resource-attributes
              [selfMode]="true"
              [isReadOnly]="false"
            ></app-resource-attributes>
          </mat-card>
        }
      </div>

      <!-- Capacity Management Section -->
      <div class="capacity-grid">
        <!-- Loading skeleton while the user profile resolves -->
        @if (isProfileLoading) {
          <mat-card
            class="capacity-management-card capacity-skeleton-card"
          >
            <div class="capacity-skeleton">
              <div class="skeleton-line xl"></div>
              <div class="skeleton-line"></div>
              <div class="skeleton-line"></div>
              <div class="skeleton-line"></div>
              <div class="skeleton-line long"></div>
            </div>
          </mat-card>
        } @else if (employeeId) {
          <!-- Show capacity management when employeeId is available -->
          <app-capacity-management
            [employeeId]="employeeId"
            [isReadOnly]="false"
            [canEditCapacitySettings]="canEditCapacitySettings"
            (editModeRequested)="onCapacityEditModeRequested()"
          ></app-capacity-management>
        } @else {
          <!-- Show message when the resolved user has no employeeId -->
          <mat-card class="capacity-management-card">
            <div class="no-capacity-message">
              <mat-icon class="no-capacity-icon">info</mat-icon>
              <h3>Capacity Management Unavailable</h3>
              <p class="no-capacity-text">
                This user cannot set capacity. Please contact your administrator.
              </p>
            </div>
          </mat-card>
        }

        <!-- Time Off Card -->
        @if (isTimeOffTrackingEnabled) {
          <mat-card class="time-off-card">
            <app-time-off-management
              [entries]="timeOffEntries"
              [lookupTypes]="timeOffTypeLookups"
              [isReadOnly]="!isTimeOffApprovalsEnabled"
              [apiMode]="'self-service'"
              [employeeId]="employeeId"
              [employeeName]="userName"
              (entriesSaved)="onTimeOffSaved()"
              (requestStatusChanged)="onTimeOffSaved()"
              (editModeRequested)="onTimeOffEditModeRequested()"
            ></app-time-off-management>
          </mat-card>

        }

        <!-- User Preferences Card -->
        <mat-card class="profile-card settings-card">
          <div class="card-header">
            <div class="card-info">
              <div class="card-value">User Preferences</div>
            </div>
          </div>
          <div class="preference-item">
            <div class="card-footer settings-footer">
              <div class="setting-header">
                <span class="setting-name">In-App Notifications</span>
                <mat-slide-toggle
                  [checked]="true"
                  color="primary"
                  class="toggle-success"
                  [hideIcon]="true"
                  [disabled]="true"
                ></mat-slide-toggle>
              </div>
              <p class="setting-description">Receive bell notifications for in-app events</p>
            </div>
          </div>
          <mat-divider></mat-divider>
          @if (isEmailNotificationsFeatureEnabled) {
            <div class="preference-item">
              <div class="card-footer settings-footer">
                <div class="setting-header">
                  <span class="setting-name">Email Notifications</span>
                  <mat-slide-toggle
                    [checked]="emailNotificationsEnabled"
                    (change)="onEmailNotificationsToggle($event.checked)"
                    color="primary"
                    class="toggle-success"
                    [hideIcon]="true"
                  ></mat-slide-toggle>
                </div>
                <p class="setting-description">Receive email notifications for in-app events</p>
                <mat-divider class="preferred-email-divider"></mat-divider>
                <div class="setting-header preferred-email-header">
                  <span class="setting-name">Preferred Email</span>
                </div>
                <p class="setting-description">Email address where notifications are sent.</p>
                <div class="preferred-email-section">
                  <div class="preferred-email-row">
                    <input
                      class="preferred-email-input"
                      type="email"
                      [formControl]="preferredEmailControl"
                      [placeholder]="preferredEmailControl.value ? '' : 'Default: ' + userEmail"
                      autocomplete="off"
                    />
                    <button
                      mat-raised-button
                      class="set-button"
                      [disabled]="preferredEmailControl.invalid || isSavingPreferredEmail || isPreferredEmailUnchanged"
                      (click)="onSetPreferredEmail()"
                    >
                      @if (isSavingPreferredEmail) {
                        <mat-spinner diameter="18"></mat-spinner>
                      } @else {
                        Set
                      }
                    </button>
                  </div>
                  @if (preferredEmailControl.invalid && preferredEmailControl.touched) {
                    <span class="preferred-email-error">Enter a valid email address</span>
                  }
                </div>
              </div>
            </div>
          }
          @if (capacityReminderVisible) {
            <mat-divider></mat-divider>
            <div class="preference-item">
              <div class="card-footer settings-footer">
                <div class="setting-header">
                  <span class="setting-name">Capacity Reminder Emails</span>
                  @if (capacityReminderLocked) {
                    <mat-slide-toggle
                      [checked]="true"
                      [disabled]="true"
                      color="primary"
                      class="toggle-success"
                      [hideIcon]="true"
                    ></mat-slide-toggle>
                  } @else {
                    <mat-slide-toggle
                      [checked]="capacityReminderEmailsEnabled"
                      (change)="onCapacityReminderEmailsToggle($event.checked)"
                      color="primary"
                      class="toggle-success"
                      [hideIcon]="true"
                    ></mat-slide-toggle>
                  }
                </div>
                <p class="setting-description">
                  @if (capacityReminderLocked) {
                    Your office requires this reminder by email, so it can't be turned off here.
                  } @else {
                    Receive your office's recurring reminder to enter your capacity by email.
                  }
                </p>
              </div>
            </div>
          }
        </mat-card>

        <!-- Notifications Card -->
        @if (isNotificationsEnabled) {
          <mat-card id="notifications-section" class="notification-card">
            <app-notification-list/>
          </mat-card>
        }

      </div>
    </div>
    <!-- Header Section -->
  </main>
</div>
